import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getGmMatchLabel, normalizeGmMatch } from '../../utils/gmMatches';
import {
  buildClosedGmMatchState,
  buildWeekWindowFromKickoff,
  getRemainingMonthlyKickoffs,
  getSeasonKickoffs,
  getSundayKickoff,
  isGmMatchExpired,
  startOfDay,
  endOfDay,
} from '../../utils/gmMatchClosing';
import { calculateGmMatchWeeklyScores } from '../../utils/gmMatchScoring';
import '../../styles/FactionRosterExperience.css';

const MATCH_TYPE_OPTIONS = [
  { value: 'single', label: '1v1', count: 2 },
  { value: 'triple_threat', label: 'Triple Threat', count: 3 },
  { value: 'fatal_4_way', label: 'Fatal 4 Way', count: 4 },
  { value: 'six_pack_challenge', label: '6-Pack Challenge', count: 6 },
  { value: 'battle_royal', label: 'Battle Royal', count: 'all' },
];

const getMatchTypeConfig = (type) =>
  MATCH_TYPE_OPTIONS.find((option) => option.value === type) || MATCH_TYPE_OPTIONS[0];

const getRequiredParticipantsCount = (type, totalFactions) => {
  const config = getMatchTypeConfig(type);
  return config.count === 'all' ? totalFactions : config.count;
};

const shuffleItems = (items) => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const normalized = new Date(date);
  const yyyy = normalized.getFullYear();
  const mm = String(normalized.getMonth() + 1).padStart(2, '0');
  const dd = String(normalized.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeMatch = (docSnapOrObject) => {
  return normalizeGmMatch(docSnapOrObject);
};

const getDefaultFormState = (factions) => {
  const kickoffAt = getSundayKickoff();
  const defaultFactions = factions.slice(0, 2).map((faction) => faction.id);

  return {
    id: null,
    matchType: 'single',
    factionIds: defaultFactions,
    kickoffDate: formatDateForInput(kickoffAt),
  };
};

const GMMatchManager = ({ rosterId, roster = null, factions = [], participants = [], isGM }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMatch, setEditingMatch] = useState(null);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [showAutoGenerator, setShowAutoGenerator] = useState(false);
  const [autoGenerator, setAutoGenerator] = useState({
    mode: 'month',
    seasonWeeks: 12,
    nonSingleCount: 0,
  });
  const [matchForm, setMatchForm] = useState(() => getDefaultFormState(factions));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const userMap = useMemo(
    () =>
      participants.reduce((map, user) => {
        map[user.id] = user;
        return map;
      }, {}),
    [participants]
  );

  const factionMap = useMemo(
    () =>
      factions.reduce((map, faction) => {
        map[faction.id] = faction;
        return map;
      }, {}),
    [factions]
  );

  const clearFeedback = () => {
    setError('');
    setSuccess('');
  };

  const setTimedSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3000);
  };

  const getParticipantDisplayName = useCallback(
    (participant) => {
      const owner = participant?.ownerUid ? userMap[participant.ownerUid] : null;
      return owner?.username || owner?.displayName || owner?.email || participant?.factionName || 'Fazione';
    },
    [userMap]
  );

  const getMatchLabel = useCallback((match) => getGmMatchLabel(match.matchType), []);

  const buildParticipantSnapshots = useCallback(
    (selectedFactionIds, matchType) => {
      const resolvedIds =
        matchType === 'battle_royal' ? factions.map((faction) => faction.id) : selectedFactionIds;

      return resolvedIds
        .map((factionId) => factionMap[factionId])
        .filter(Boolean)
        .map((faction) => ({
          factionId: faction.id,
          factionName: faction.name,
          ownerUid: faction.ownerUid || null,
        }));
    },
    [factionMap, factions]
  );

  const buildMatchPayload = useCallback(
    (matchType, selectedFactionIds, kickoffAt) => {
      const participantSnapshots = buildParticipantSnapshots(selectedFactionIds, matchType);
      const { weekStart, weekEnd } = buildWeekWindowFromKickoff(kickoffAt);

      return {
        matchType,
        participants: participantSnapshots,
        kickoffAt: Timestamp.fromDate(kickoffAt),
        weekStart: Timestamp.fromDate(weekStart),
        weekEnd: Timestamp.fromDate(weekEnd),
        status: 'open',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        results: [],
        winnerIds: [],
        winnerNames: [],
        winnerPlayerNames: [],
        autoClose: true,
      };
    },
    [buildParticipantSnapshots]
  );

  const getFactionWeeklyScores = useCallback(
    async (match) => {
      const weekStart = match.weekStart ? startOfDay(match.weekStart) : null;
      const weekEnd = match.weekEnd ? endOfDay(match.weekEnd) : null;

      if (!weekStart || !weekEnd) {
        throw new Error('Periodo match non valido');
      }

      const rostersByFactionId = {};
      await Promise.all(
        match.participants.map(async (participant) => {
          const faction = factionMap[participant.factionId];

          if (faction?.rosterId && faction.rosterId !== roster?.id) {
            const rosterSnap = await getDoc(doc(db, 'rosters', faction.rosterId));

            if (rosterSnap.exists()) {
              rostersByFactionId[participant.factionId] = {
                id: rosterSnap.id,
                ...rosterSnap.data(),
              };
              return;
            }
          }

          if (roster) {
            rostersByFactionId[participant.factionId] = roster;
          }
        })
      );

      const showsQuery = query(
        collection(db, 'shows'),
        where('data', '>=', Timestamp.fromDate(weekStart)),
        where('data', '<=', Timestamp.fromDate(weekEnd))
      );

      const showsSnapshot = await getDocs(showsQuery);
      const showEntries = await Promise.all(
        showsSnapshot.docs.map(async (showDoc) => {
          const participationsSnapshot = await getDocs(
            collection(db, 'shows', showDoc.id, 'participations')
          );

          return {
            show: { id: showDoc.id, ...showDoc.data() },
            participations: participationsSnapshot.docs.map((participationDoc) => ({
              id: participationDoc.id,
              ...participationDoc.data(),
            })),
          };
        })
      );

      return calculateGmMatchWeeklyScores({
        match,
        factionsById: factionMap,
        rostersByFactionId,
        showEntries,
      });
    },
    [factionMap, roster]
  );

  const closeMatch = useCallback(
    async (match, closedAt = new Date()) => {
      const weeklyScores = await getFactionWeeklyScores(match);
      const closedState = buildClosedGmMatchState({
        match,
        weeklyScoresByFaction: weeklyScores,
        getParticipantDisplayName,
        closedAt,
      });

      await updateDoc(doc(db, 'rosters', rosterId, 'matches', match.id), {
        ...closedState,
        closedAt: Timestamp.fromDate(closedState.closedAt),
        updatedAt: Timestamp.now(),
      });
    },
    [getFactionWeeklyScores, getParticipantDisplayName, rosterId]
  );

  const autoCloseExpiredMatches = useCallback(
    async (normalizedMatches) => {
      const now = new Date();
      const expiredMatches = normalizedMatches.filter((match) => isGmMatchExpired(match, now));

      if (expiredMatches.length === 0) return false;

      for (const match of expiredMatches) {
        await closeMatch(match, now);
      }

      return true;
    },
    [closeMatch]
  );

  const loadMatches = useCallback(async () => {
    if (!rosterId) return;
    setLoading(true);

    try {
      const matchesRef = collection(db, 'rosters', rosterId, 'matches');
      let snapshot = await getDocs(matchesRef);
      let matchesData = snapshot.docs.map((docSnap) => normalizeMatch(docSnap));

      const hasAutoClosedMatches = await autoCloseExpiredMatches(matchesData);
      if (hasAutoClosedMatches) {
        snapshot = await getDocs(matchesRef);
        matchesData = snapshot.docs.map((docSnap) => normalizeMatch(docSnap));
      }

      setMatches(matchesData);
    } catch (err) {
      console.error('Errore caricamento match:', err);
      setError('Impossibile caricare i match GM');
    } finally {
      setLoading(false);
    }
  }, [autoCloseExpiredMatches, rosterId]);

  useEffect(() => {
    if (rosterId && isGM) {
      loadMatches();
    }
  }, [isGM, loadMatches, rosterId]);

  useEffect(() => {
    setMatchForm((prev) => {
      const availableFactionIds = new Set(factions.map((faction) => faction.id));
      const filteredIds = (prev.factionIds || []).filter((id) => availableFactionIds.has(id));

      return {
        ...prev,
        factionIds: filteredIds,
      };
    });
  }, [factions]);

  const openCreateMatchForm = () => {
    clearFeedback();
    setEditingMatch(null);
    setMatchForm(getDefaultFormState(factions));
    setShowMatchForm(true);
  };

  const generateRandomMatch = async () => {
    clearFeedback();

    if (factions.length < 2) {
      setError('Sono necessarie almeno due fazioni per generare un match 1v1');
      return;
    }

    const kickoffAt = getSundayKickoff();
    const selected = shuffleItems(factions).slice(0, 2).map((faction) => faction.id);
    const payload = buildMatchPayload('single', selected, kickoffAt);

    try {
      await addDoc(collection(db, 'rosters', rosterId, 'matches'), payload);
      await loadMatches();
      setTimedSuccess('Match 1v1 generato per la prossima settimana GM');
    } catch (err) {
      console.error('Errore generazione match:', err);
      setError('Errore durante la generazione del match.');
    }
  };

  const openAutoGenerator = (mode) => {
    clearFeedback();
    setAutoGenerator((prev) => ({
      ...prev,
      mode,
      nonSingleCount: mode === 'season' ? prev.nonSingleCount : 0,
    }));
    setShowAutoGenerator(true);
  };

  const generateAutomaticMatches = async () => {
    clearFeedback();

    if (factions.length < 2) {
      setError('Sono necessarie almeno due fazioni per generare match automatici');
      return;
    }

    const mode = autoGenerator.mode;
    const seasonWeeks = Math.max(1, Number(autoGenerator.seasonWeeks) || 12);
    const kickoffs = mode === 'season'
      ? getSeasonKickoffs(new Date(), seasonWeeks)
      : getRemainingMonthlyKickoffs(new Date());

    if (kickoffs.length === 0) {
      setError('Non ci sono domeniche disponibili nel periodo selezionato');
      return;
    }

    const existingKickoffKeys = new Set(
      matches
        .map((match) => match.kickoffAt)
        .filter(Boolean)
        .map((date) => startOfDay(date).getTime())
    );
    const availableKickoffs = kickoffs.filter(
      (kickoffAt) => !existingKickoffKeys.has(startOfDay(kickoffAt).getTime())
    );

    if (availableKickoffs.length === 0) {
      setError('Tutte le domeniche del periodo selezionato hanno gia un match programmato');
      return;
    }

    const validSpecialTypes = MATCH_TYPE_OPTIONS.filter((option) => {
      if (option.value === 'single') return false;
      return option.count === 'all' ? factions.length >= 2 : option.count <= factions.length;
    });
    const requestedSpecialCount = mode === 'season'
      ? Math.max(0, Math.min(Number(autoGenerator.nonSingleCount) || 0, availableKickoffs.length))
      : 0;
    const specialCount = validSpecialTypes.length > 0 ? requestedSpecialCount : 0;
    const specialIndexes = new Set(
      shuffleItems(Array.from({ length: availableKickoffs.length }, (_, index) => index)).slice(0, specialCount)
    );

    try {
      const matchesRef = collection(db, 'rosters', rosterId, 'matches');

      await Promise.all(
        availableKickoffs.map((kickoffAt, index) => {
          const useSpecialType = specialIndexes.has(index);
          const matchType = useSpecialType
            ? shuffleItems(validSpecialTypes)[0].value
            : 'single';
          const requiredCount = getRequiredParticipantsCount(matchType, factions.length);
          const selectedFactionIds = matchType === 'battle_royal'
            ? factions.map((faction) => faction.id)
            : shuffleItems(factions).slice(0, requiredCount).map((faction) => faction.id);

          return addDoc(matchesRef, buildMatchPayload(matchType, selectedFactionIds, kickoffAt));
        })
      );

      setShowAutoGenerator(false);
      await loadMatches();
      setTimedSuccess(
        mode === 'season'
          ? `Creati ${availableKickoffs.length} match automatici per la stagione (${specialCount} non 1v1).`
          : `Creati ${availableKickoffs.length} match automatici per il mese corrente.`
      );
    } catch (err) {
      console.error('Errore generazione automatica match:', err);
      setError('Errore durante la generazione automatica dei match.');
    }
  };

  const openEditMatch = (match) => {
    clearFeedback();
    setEditingMatch(match);
    setMatchForm({
      id: match.id,
      matchType: match.matchType || 'single',
      factionIds: match.participants.map((participant) => participant.factionId),
      kickoffDate: formatDateForInput(match.kickoffAt || getSundayKickoff()),
    });
    setShowMatchForm(true);
  };

  const updateFactionSelection = (index, factionId) => {
    setMatchForm((prev) => {
      const nextFactionIds = [...prev.factionIds];
      nextFactionIds[index] = factionId;
      return {
        ...prev,
        factionIds: nextFactionIds,
      };
    });
  };

  const saveMatch = async () => {
    clearFeedback();

    const { matchType, kickoffDate, factionIds } = matchForm;
    const kickoffAt = kickoffDate ? startOfDay(new Date(kickoffDate)) : null;

    if (!kickoffAt || Number.isNaN(kickoffAt.getTime())) {
      setError('Seleziona una domenica valida per l’avvio del match');
      return;
    }

    if (kickoffAt.getDay() !== 0) {
      setError('Il match GM deve partire di domenica');
      return;
    }

    const requiredParticipants = getRequiredParticipantsCount(matchType, factions.length);
    if (matchType === 'battle_royal') {
      if (factions.length < 2) {
        setError('Servono almeno due fazioni per una battle royal');
        return;
      }
    } else if (factionIds.length !== requiredParticipants) {
      setError(`Questo match richiede ${requiredParticipants} fazioni`);
      return;
    }

    const selectedFactionIds =
      matchType === 'battle_royal' ? factions.map((faction) => faction.id) : factionIds;

    const uniqueFactionIds = [...new Set(selectedFactionIds.filter(Boolean))];
    if (uniqueFactionIds.length !== selectedFactionIds.length) {
      setError('Le fazioni selezionate devono essere tutte diverse');
      return;
    }

    const participantSnapshots = buildParticipantSnapshots(uniqueFactionIds, matchType);
    if (participantSnapshots.length < 2) {
      setError('Le fazioni selezionate non sono sufficienti per il match');
      return;
    }

    const { weekStart, weekEnd } = buildWeekWindowFromKickoff(kickoffAt);
    const payload = {
      matchType,
      participants: participantSnapshots,
      kickoffAt: Timestamp.fromDate(kickoffAt),
      weekStart: Timestamp.fromDate(weekStart),
      weekEnd: Timestamp.fromDate(weekEnd),
      updatedAt: Timestamp.now(),
      autoClose: true,
    };

    try {
      if (editingMatch?.id) {
        await updateDoc(doc(db, 'rosters', rosterId, 'matches', editingMatch.id), payload);
        setTimedSuccess('Match GM aggiornato');
      } else {
        await addDoc(collection(db, 'rosters', rosterId, 'matches'), {
          ...payload,
          status: 'open',
          createdAt: Timestamp.now(),
          results: [],
          winnerIds: [],
          winnerNames: [],
          winnerPlayerNames: [],
        });
        setTimedSuccess('Match GM creato');
      }

      setShowMatchForm(false);
      setEditingMatch(null);
      await loadMatches();
    } catch (err) {
      console.error('Errore salvataggio match:', err);
      setError('Errore durante il salvataggio del match');
    }
  };

  const deleteMatch = async (matchId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo match GM?')) return;

    try {
      await deleteDoc(doc(db, 'rosters', rosterId, 'matches', matchId));
      await loadMatches();
      setTimedSuccess('Match eliminato');
    } catch (err) {
      console.error('Errore eliminazione match:', err);
      setError('Errore durante l’eliminazione del match');
    }
  };

  const recalculateClosedMatch = async (match) => {
    clearFeedback();

    try {
      await closeMatch(match, new Date());
      await loadMatches();
      setTimedSuccess('Risultato GM ricalcolato con i punti reali della settimana');
    } catch (err) {
      console.error('Errore ricalcolo match:', err);
      setError('Errore durante il ricalcolo del match');
    }
  };

  const openMatches = [...matches]
    .filter((match) => match.status === 'open')
    .sort((a, b) => (a.weekStart?.getTime?.() || 0) - (b.weekStart?.getTime?.() || 0));

  const closedMatches = [...matches]
    .filter((match) => match.status === 'closed')
    .sort((a, b) => (b.closedAt?.getTime?.() || 0) - (a.closedAt?.getTime?.() || 0));

  const requiredSelectionsForForm = useMemo(() => {
    const count = getRequiredParticipantsCount(matchForm.matchType, factions.length);
    if (count === 'all') return [];
    return Array.from({ length: count }, (_, index) => index);
  }, [factions.length, matchForm.matchType]);

  const isBattleRoyal = matchForm.matchType === 'battle_royal';
  const nextKickoffLabel = openMatches[0]?.kickoffAt
    ? openMatches[0].kickoffAt.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })
    : 'Nessun match programmato';
  const formInsights = useMemo(() => {
      const blockers = [];
      const warnings = [];
      const selectedFactionIds =
        matchForm.matchType === 'battle_royal'
          ? factions.map((faction) => faction.id)
          : (matchForm.factionIds || []).filter(Boolean);
      const uniqueSelectedFactionIds = [...new Set(selectedFactionIds)];
      const kickoffAt = matchForm.kickoffDate ? startOfDay(new Date(matchForm.kickoffDate)) : null;
      const requiredParticipants = getRequiredParticipantsCount(matchForm.matchType, factions.length);

      if (!kickoffAt || Number.isNaN(kickoffAt.getTime())) {
        blockers.push('Scegli una domenica valida per avviare il match.');
      } else if (kickoffAt.getDay() !== 0) {
        blockers.push('Il kickoff del match GM deve essere fissato di domenica.');
      }

      if (matchForm.matchType === 'battle_royal') {
        if (factions.length < 2) {
          blockers.push('Per una battle royal servono almeno due fazioni nel roster.');
        }
      } else {
        if (requiredParticipants > factions.length) {
          blockers.push(`Il formato selezionato richiede ${requiredParticipants} fazioni, ma nel roster ce ne sono solo ${factions.length}.`);
        }
        if (selectedFactionIds.length < requiredParticipants) {
          blockers.push(`Seleziona ${requiredParticipants} fazioni per completare il match.`);
        }
        if (uniqueSelectedFactionIds.length !== selectedFactionIds.length) {
          blockers.push('Ogni slot del match deve contenere una fazione diversa.');
        }
      }

      if (selectedFactionIds.some((factionId) => !factionMap[factionId])) {
        warnings.push('Una delle fazioni selezionate non risulta piu disponibile nel roster.');
      }

      if (kickoffAt && !Number.isNaN(kickoffAt.getTime()) && uniqueSelectedFactionIds.length > 0) {
        const today = startOfDay(new Date());
        if (kickoffAt.getTime() < today.getTime()) {
          warnings.push('Stai programmando un kickoff in una data gia trascorsa.');
        }

        const { weekStart, weekEnd } = buildWeekWindowFromKickoff(kickoffAt);
        const overlappingMatches = openMatches.filter((match) => {
          if (editingMatch?.id && match.id === editingMatch.id) return false;

          const matchFactionIds = match.participants.map((participant) => participant.factionId);
          const hasFactionOverlap = uniqueSelectedFactionIds.some((factionId) => matchFactionIds.includes(factionId));
          if (!hasFactionOverlap) return false;

          return (
            startOfDay(match.weekStart).getTime() === weekStart.getTime() &&
            endOfDay(match.weekEnd).getTime() === weekEnd.getTime()
          );
        });

        if (overlappingMatches.length > 0) {
          warnings.push('Una o piu fazioni selezionate hanno gia un match aperto nella stessa settimana GM.');
        }
      }

      return {
        blockers,
        warnings,
        canSave: blockers.length === 0,
      };
    }, [editingMatch?.id, factionMap, factions, matchForm.factionIds, matchForm.kickoffDate, matchForm.matchType, openMatches]);

  if (!isGM) return null;

  return (
    <div className="card shadow-sm mb-4 frx-surface">
      <div className="card-header gm-match-manager__header">
        <div className="gm-match-manager__shell">
          <div className="gm-match-manager__intro">
            <div className="gm-match-manager__copy">
              <h4 className="gm-match-manager__title">Gestione Match</h4>
              <p className="gm-match-manager__subtitle">
                Crea, programma e controlla i match settimanali del roster da un unico punto.
              </p>
            </div>

            <div className="gm-match-manager__stats" aria-label="Riepilogo match GM">
              <div className="gm-match-manager__stat">
                <span className="gm-match-manager__stat-label">Aperti</span>
                <strong className="gm-match-manager__stat-value">{openMatches.length}</strong>
                <span className="gm-match-manager__stat-helper">Kickoff: {nextKickoffLabel}</span>
              </div>
              <div className="gm-match-manager__stat">
                <span className="gm-match-manager__stat-label">Chiusi</span>
                <strong className="gm-match-manager__stat-value">{closedMatches.length}</strong>
              </div>
              <div className="gm-match-manager__stat">
                <span className="gm-match-manager__stat-label">Fazioni</span>
                <strong className="gm-match-manager__stat-value">{factions.length}</strong>
                <span className="gm-match-manager__stat-helper">Supporto fino alla battle royal</span>
              </div>
            </div>
          </div>

          <div className="gm-match-manager__actions">
            <button
              className="btn btn-light gm-match-manager__action gm-match-manager__action--primary"
              onClick={openCreateMatchForm}
            >
              <i className="bi bi-plus-circle"></i>
              Nuovo match
            </button>

            <div className="gm-match-manager__secondary-actions">
              <button
                className="btn btn-outline-light gm-match-manager__action"
                onClick={generateRandomMatch}
              >
                <i className="bi bi-shuffle"></i>
                Genera 1v1
              </button>
              <button
                className="btn btn-outline-light gm-match-manager__action"
                onClick={() => openAutoGenerator('month')}
              >
                <i className="bi bi-calendar-month"></i>
                Genera mese
              </button>
              <button
                className="btn btn-outline-light gm-match-manager__action"
                onClick={() => openAutoGenerator('season')}
              >
                <i className="bi bi-calendar3"></i>
                Genera stagione
              </button>
            </div>
          </div>

          <div className="gm-match-manager__note">
            <i className="bi bi-info-circle"></i>
            <span>
              I match GM partono di <strong>domenica</strong>, accumulano i punti degli show da
              <strong> lunedi a domenica</strong> e si chiudono automaticamente a fine settimana.
            </span>
          </div>
        </div>
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger frx-alert">{error}</div>}
        {success && <div className="alert alert-success frx-alert">{success}</div>}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        {!loading && (
          <>
            <h5 className="mt-0 mb-3 text-brand-secondary">Match aperti</h5>
            {openMatches.length === 0 ? (
              <div className="frx-empty">
                <i className="bi bi-calendar-x display-6"></i>
                <h4 className="brand-title h5 mb-0">Nessun match GM aperto</h4>
                <p className="mb-0">Genera un 1v1 automatico oppure crea manualmente il primo match.</p>
              </div>
            ) : (
              <div className="row g-3">
                {openMatches.map((match) => (
                  <div key={match.id} className="col-12 col-lg-6">
                    <div className="card h-100 border-0 shadow-sm frx-match-card" style={{ background: 'rgba(0, 127, 177, 0.05)' }}>
                      <div className="card-body d-flex flex-column">
                        {/* Badge */}
                        <div className="mb-3 text-center">
                          <span className="badge bg-brand-primary-ultralight px-3 py-2 rounded-pill text-brand-primary">
                            {getMatchLabel(match)}
                          </span>
                        </div>

                        {/* Griglia responsiva per i partecipanti */}
                        <div className="grid-participants mb-4" style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                          gap: '1rem'
                        }}>
                          {match.participants.map((participant, idx) => (
                            <div key={idx} className="text-center">
                              <div className="fw-bold fs-6 text-brand-secondary">
                                {getParticipantDisplayName(participant)}
                              </div>
                              <div className="small text-brand-muted">
                                {participant.factionName}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Date centrate */}
                        <div className="text-center my-2 py-2  border-bottom border-secondary">
                          <div className="small text-light">
                            <i className="bi bi-calendar-event me-1"></i>
                            {match.kickoffAt?.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' }) || '—'}
                            {' – '}
                            {match.weekEnd?.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' }) || '—'}
                          </div>
                        </div>

                        {/* Pulsanti laterali */}
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <button
                            className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px' }}
                            onClick={() => deleteMatch(match.id)}
                            title="Elimina match"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px' }}
                            onClick={() => openEditMatch(match)}
                            title="Modifica match"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h5 className="mt-4 mb-3 text-brand-secondary ">Match chiusi</h5>
            {closedMatches.length === 0 ? (
              <div className="frx-empty">
                <i className="bi bi-inbox display-6"></i>
                <h4 className="brand-title h5 mb-0">Nessun match GM chiuso</h4>
                <p className="mb-0">Quando i match terminano, i risultati compariranno qui con i punti assegnati.</p>
              </div>
            ) : (
              <div className="table-responsive frx-table-wrap">
                <table className="table table-sm table-dark align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Tipo</th>
                      <th>Partecipanti</th>
                      <th>Punti settimanali</th>
                      <th>Esito</th>
                      <th>Chiusura</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedMatches.map((match) => (
                      <tr key={match.id}>
                        <td>{getMatchLabel(match)}</td>
                        <td>
                          {(match.results || match.participants).map((participant) => (
                            <div key={participant.factionId} className="small text-light">
                              {getParticipantDisplayName(participant)} ({participant.factionName})
                            </div>
                          ))}
                        </td>
                        <td>
                          {(match.results || []).length > 0 ? (
                            match.results.map((result) => (
                              <div key={result.factionId} className="small">
                                {result.factionName}: <strong>{result.weeklyScore}</strong>
                              </div>
                            ))
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {(match.results || []).length > 0 ? (
                            match.results.map((result) => (
                              <div key={result.factionId} className="small">
                                {result.factionName}:{' '}
                                <span className={`badge ${result.isWinner ? 'bg-success' : 'bg-secondary'}`}>
                                  {result.matchPoints} pt match
                                </span>
                              </div>
                            ))
                          ) : match.winnerPlayerName ? (
                            <span className="badge bg-success">{match.winnerPlayerName}</span>
                          ) : (
                            <span className="badge bg-secondary">Pareggio</span>
                          )}
                        </td>
                        <td>{match.closedAt?.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' }) || '—'}</td>                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => recalculateClosedMatch(match)}
                              title="Ricalcola dai punti degli show"
                            >
                              <i className="bi bi-arrow-repeat"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => deleteMatch(match.id)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {showMatchForm && (
        <div className="modal fade show d-block" style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content frx-surface text-light border-0">
              <div className="modal-header bg-brand-primary text-white border-0">
                <h5 className="modal-title">{editingMatch ? 'Modifica Match GM' : 'Nuovo Match GM'}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowMatchForm(false)}></button>
              </div>
              <div className="modal-body">

                {formInsights.warnings.length > 0 && (
                  <div className="alert alert-warning frx-alert bg-warning bg-opacity-10 text-light border-0">
                    <div className="fw-semibold mb-2">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Avvisi utili
                    </div>
                    {formInsights.warnings.map((item) => (
                      <div key={item} className="small">{item}</div>
                    ))}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Tipo match</label>
                  <select
                    className="form-select"
                    value={matchForm.matchType}
                    onChange={(e) =>
                      setMatchForm((prev) => ({
                        ...prev,
                        matchType: e.target.value,
                        factionIds:
                          e.target.value === 'battle_royal'
                            ? factions.map((faction) => faction.id)
                            : prev.factionIds.slice(
                              0,
                              getRequiredParticipantsCount(e.target.value, factions.length)
                            ),
                      }))
                    }
                  >
                    {MATCH_TYPE_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.count !== 'all' && option.count > factions.length}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Domenica di avvio</label>
                  <input
                    type="date"
                    className="form-control"
                    value={matchForm.kickoffDate}
                    onChange={(e) => setMatchForm((prev) => ({ ...prev, kickoffDate: e.target.value }))}
                  />
                  <div className="form-text text-brand-muted">
                    Il match partirà questa domenica e conterà i punti degli show dal lunedì alla
                    domenica successiva.
                  </div>
                </div>

                {isBattleRoyal ? (
                  <div className="alert alert-info frx-alert bg-info bg-opacity-10 text-light mb-0">
                    In battle royal partecipano automaticamente tutte le fazioni del roster:
                    <div className="mt-2 small">{factions.map((faction) => faction.name).join(', ')}</div>
                  </div>
                ) : (
                  requiredSelectionsForForm.map((index) => (
                    <div className="mb-3" key={`faction-slot-${index}`}>
                      <label className="form-label">Fazione {index + 1}</label>
                      <select
                        className="form-select"
                        value={matchForm.factionIds[index] || ''}
                        onChange={(e) => updateFactionSelection(index, e.target.value)}
                      >
                        <option value="">Seleziona una fazione</option>
                        {factions.map((faction) => {
                          const owner = userMap[faction.ownerUid];
                          return (
                            <option key={faction.id} value={faction.id}>
                              {(owner?.username || owner?.email || faction.name) + ` (${faction.name})`}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-secondary" onClick={() => setShowMatchForm(false)}>
                  Annulla
                </button>
                <button
                  className="btn btn-primary"
                  onClick={saveMatch}
                  disabled={!formInsights.canSave}
                  title={!formInsights.canSave ? 'Completa prima i controlli richiesti' : ''}
                >
                  {editingMatch ? 'Aggiorna match' : 'Salva match'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAutoGenerator && (
        <div className="modal fade show d-block" style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content frx-surface text-light border-0">
              <div className="modal-header bg-brand-primary text-white border-0">
                <h5 className="modal-title">
                  {autoGenerator.mode === 'season' ? 'Genera match stagione' : 'Genera match del mese'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAutoGenerator(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info frx-alert bg-info bg-opacity-10 text-light border-0">
                  {autoGenerator.mode === 'season'
                    ? 'Verrà creato un match casuale per ogni domenica della stagione generata. Le domeniche gia occupate verranno saltate.'
                    : 'Verrà creato un match 1v1 casuale per ogni domenica rimanente del mese corrente. Le domeniche gia occupate verranno saltate.'}
                </div>

                {autoGenerator.mode === 'season' && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Settimane da generare</label>
                      <input
                        type="number"
                        min="1"
                        max="52"
                        className="form-control"
                        value={autoGenerator.seasonWeeks}
                        onChange={(event) =>
                          setAutoGenerator((prev) => ({
                            ...prev,
                            seasonWeeks: Math.max(1, Number(event.target.value) || 1),
                          }))
                        }
                      />
                    </div>

                    <div className="mb-0">
                      <label className="form-label">Match diversi da 1v1</label>
                      <input
                        type="number"
                        min="0"
                        max={Math.max(0, Number(autoGenerator.seasonWeeks) || 12)}
                        className="form-control"
                        value={autoGenerator.nonSingleCount}
                        onChange={(event) =>
                          setAutoGenerator((prev) => ({
                            ...prev,
                            nonSingleCount: Math.max(0, Number(event.target.value) || 0),
                          }))
                        }
                      />
                      <div className="form-text text-brand-muted">
                        I match speciali saranno scelti casualmente tra i formati disponibili per il numero di fazioni del roster.
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-secondary" onClick={() => setShowAutoGenerator(false)}>
                  Annulla
                </button>
                <button className="btn btn-primary" onClick={generateAutomaticMatches}>
                  Genera match
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GMMatchManager;
